// components/Admin/RoomManagement.jsx
// Examination Room / Equipment registry — backs the Scheduling Board's room columns
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Snackbar, CircularProgress, Chip, MenuItem
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Save as SaveIcon, Close as CloseIcon
} from '@mui/icons-material';
import { roomService } from '../../services/ris';
import { useThemeMode } from '../../contexts/ThemeModeContext';

const MODALITIES = ['CT', 'MRI', 'XRAY', 'MAMMO', 'US', 'ANGIO', 'BMD', 'FLUORO', 'CR', 'DR', 'MR', 'MG', 'PT', 'NM', 'OT', 'DX'];

const emptyForm = { name: '', modality: '', notes: '' };

export default function RoomManagement() {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchRooms = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await roomService.list();
      setRooms(data.rooms || []);
    } catch (err) {
      console.error('Failed to load rooms:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (room) => { setEditing(room); setForm({ name: room.name, modality: room.modality, notes: room.notes || '' }); setDialogOpen(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        await roomService.update(editing._id, form);
        setSnackbar({ open: true, message: 'Room updated', severity: 'success' });
      } else {
        await roomService.create(form);
        setSnackbar({ open: true, message: 'Room created', severity: 'success' });
      }
      setDialogOpen(false);
      fetchRooms();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to save', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this room?')) return;
    try {
      await roomService.delete(id);
      setSnackbar({ open: true, message: 'Room deactivated', severity: 'success' });
      fetchRooms();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to deactivate', severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>Rooms & Equipment</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Room</Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Modality</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : rooms.length === 0 ? (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">No rooms. Click "Add Room" to create one.</Typography>
              </TableCell></TableRow>
            ) : rooms.map(r => (
              <TableRow key={r._id} hover>
                <TableCell>{r.name}</TableCell>
                <TableCell><Chip label={r.modality} size="small" /></TableCell>
                <TableCell>{r.notes}</TableCell>
                <TableCell>
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Deactivate"><IconButton size="small" color="error" onClick={() => handleDelete(r._id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '460px' },
            maxWidth: '480px !important',
            maxHeight: '85vh',
            borderRadius: '14px',
            bgcolor: isDark ? '#1E1E1E' : '#FFFFFF',
            backgroundImage: 'none',
            color: isDark ? '#F1F5F9' : '#0F172A',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            px: 2.5,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider',
            fontSize: '1.05rem',
            fontWeight: 700
          }}
        >
          {editing ? 'Edit Room' : 'New Room'}
          <IconButton size="small" onClick={() => setDialogOpen(false)} sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 1.5, overflowY: 'auto' }}>
          <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
            <Grid item xs={12}>
              <TextField fullWidth required label="Name" placeholder="e.g. CT Suite 1" value={form.name} size="small"
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required select label="Modality" value={form.modality} size="small"
                onChange={e => setForm(f => ({ ...f, modality: e.target.value }))}>
                {MODALITIES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} maxRows={3} label="Notes" value={form.notes} size="small"
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 1.25,
            borderTop: 1,
            borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1
          }}
        >
          <Button onClick={() => setDialogOpen(false)} sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
